import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { PencilRuler } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import LoadingSkeleton from "@/modules/common/components/ui/loadingSkeleton";
import CommonEmptyState from "../common/empty-state";
import DrawingRequestCard from "./drawing-request-card";
import {
  useGetDrawingRequests,
  useGetProjectByProperty,
  useCreateDrawingRequest,
  useMarkDrawingRequestAsFinal,
  useUnmarkDrawingRequestAsFinal,
} from "../../hooks/drawing-requests/use-drawing-requests.hook";
import { DrawingRequestStatus, ProjectStatus } from "../../types";

function CreateDrawingRequestDialog({
  open,
  onClose,
  onSubmit,
  isLoading,
}: Readonly<{
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: { description?: string; file?: File }) => void;
  isLoading: boolean;
}>) {
  const [description, setDescription] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) {
      setDescription("");
      setFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  }, [open]);

  const handleSubmit = () => {
    onSubmit({
      description: description.trim() || undefined,
      file: file ?? undefined,
    });
    setDescription("");
    setFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="tablet" fullWidth>
      <DialogTitle>Create Drawing Request</DialogTitle>
      <DialogContent>
        <Stack gap={2} sx={{ pt: 1 }}>
          <TextField
            autoFocus
            margin="dense"
            label="Description (optional)"
            fullWidth
            variant="outlined"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <input
            ref={fileInputRef}
            type="file"
            hidden
            onChange={(e) => {
              const next = e.target.files?.[0];
              setFile(next ?? null);
            }}
          />
          <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
            <Button
              variant="outlined"
              size="small"
              sx={{ textTransform: "none" }}
              onClick={() => fileInputRef.current?.click()}
            >
              Choose file
            </Button>
            <Typography variant="body2" color="text.secondary">
              {file ? file.name : "No file selected (optional)"}
            </Typography>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={isLoading}>
          {isLoading ? "Creating..." : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function DrawingProposalsTab({
  propertyUid,
  customerUid,
  isContractSigned,
}: Readonly<{
  propertyUid: string;
  customerUid: string;
  isContractSigned: boolean;
}>) {
  const { data: drawingRequests, isLoading } = useGetDrawingRequests({
    propertyUid,
  });
  const { data: project } = useGetProjectByProperty({ propertyUid });
  const createMutation = useCreateDrawingRequest({ propertyUid });
  const markAsFinalMutation = useMarkDrawingRequestAsFinal({ propertyUid });
  const unmarkAsFinalMutation = useUnmarkDrawingRequestAsFinal({ propertyUid });
  const [dialogOpen, setDialogOpen] = useState(false);

  const isActionsBlocked = !(
    !project || project.status === ProjectStatus.PENDING_SIGNATURE
  );

  const hasPendingArchitecture = drawingRequests?.some(
    (r) => r.status === DrawingRequestStatus.PENDING_ARCHITECTURE,
  );
  const hasApprovedProposal = drawingRequests?.some(
    (r) => r.isApprovedForExecution,
  );
  const isNewRequestDisabled =
    !!hasPendingArchitecture || !!hasApprovedProposal || isActionsBlocked;

  const handleCreate = (payload: { description?: string; file?: File }) => {
    createMutation.mutate(
      { ...payload, customerUid },
      {
        onSuccess: () => setDialogOpen(false),
      },
    );
  };

  if (isLoading) {
    return <LoadingSkeleton columns={1} rows={3} />;
  }

  const requests = drawingRequests ?? [];
  const isEmpty = requests.length === 0;

  return (
    <Stack gap={(theme) => theme.nawy.spacing.md} width="100%" height="100%">
      {isEmpty ? (
        <CommonEmptyState
          icon={<PencilRuler />}
          title="No Drawing Proposals yet"
          descrption="This property has no drawing proposals yet."
          callToAction={
            <Button
              variant="contained"
              size="small"
              disabled={isActionsBlocked}
              onClick={() => setDialogOpen(true)}
              sx={{ textTransform: "none" }}
            >
              + New Request
            </Button>
          }
        />
      ) : (
        <>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={(theme) => ({
              bgcolor: theme.nawy.colors.semantic.layer.primary,
              borderRadius: theme.nawy.radius.xl,
              p: theme.nawy.spacing.md,
            })}
          >
            <Typography
              sx={(th) => th.nawy.typography.english.variants.headline.xs}
            >
              Drawing Proposals
            </Typography>
            <Button
              variant="contained"
              size="small"
              disabled={isNewRequestDisabled}
              onClick={() => setDialogOpen(true)}
              sx={{ textTransform: "none" }}
            >
              + New Request
            </Button>
          </Stack>

          <Stack gap={(theme) => theme.nawy.spacing.sm}>
            {requests.map((request) => (
              <DrawingRequestCard
                key={request.uid}
                request={request}
                onMarkAsFinal={(uid) => markAsFinalMutation.mutate(uid)}
                isMarkingFinal={markAsFinalMutation.isPending}
                anyApproved={!!hasApprovedProposal}
                onUnmarkAsFinal={(uid) => unmarkAsFinalMutation.mutate(uid)}
                isUnmarkingFinal={unmarkAsFinalMutation.isPending}
                isContractSigned={isContractSigned}
                isActionsBlocked={isActionsBlocked}
              />
            ))}
          </Stack>
        </>
      )}

      <CreateDrawingRequestDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSubmit={handleCreate}
        isLoading={createMutation.isPending}
      />
    </Stack>
  );
}
